import { useEffect, useState } from "react";

/** Default thresholds. Dev mode uses higher transfer limit (unminified bundles, source maps, HMR). */
const DEFAULT_MAX_TRANSFER_BYTES =
  process.env.NODE_ENV === "development" ? 15 * 1024 * 1024 : 5 * 1024 * 1024; // 15MB dev, 5MB prod
const DEFAULT_MAX_REQUESTS = 150;

export interface UsePageWeightWarningsOptions {
  /** Max total transfer size in bytes before warning. Default 5MB. */
  maxTransferBytes?: number;
  /** Max number of requests before warning. Default 150. */
  maxRequests?: number;
}

export interface LargestResource {
  /** Short display name (basename of URL, e.g. app.js). */
  name: string;
  /** Transfer size in bytes. */
  transferSize: number;
}

export interface PageWeightMetrics {
  /** Total transfer size in bytes (excluding largest resource, typically app.js). */
  transferBytes: number;
  /** Number of resource requests. */
  requestCount: number;
  /** Largest single resource, excluded from transferBytes (app bundle; not in our control in dev). */
  excludedResource?: LargestResource;
}

export interface UsePageWeightWarningsResult {
  /** User-facing page weight warning strings (empty until run, or when within limits). */
  warnings: string[];
  /** True only after the check has completed (after load). */
  hasRun: boolean;
  /** Measured values when hasRun is true (for display in passed message). */
  metrics?: PageWeightMetrics;
}

/**
 * Sums resource transfer sizes and request count after load (dev only);
 * excludes the largest single resource (typically app.js) so we measure only
 * resources within our control (images, fonts, etc.). Returns warnings when above limits.
 *
 * @param options - Optional thresholds (maxTransferBytes, maxRequests).
 * @returns Object with warnings array and hasRun true only after the check completed.
 */
export function usePageWeightWarnings(
  options: UsePageWeightWarningsOptions = {},
): UsePageWeightWarningsResult {
  const {
    maxTransferBytes = DEFAULT_MAX_TRANSFER_BYTES,
    maxRequests = DEFAULT_MAX_REQUESTS,
  } = options;
  const [warnings, setWarnings] = useState<string[]>([]);
  const [hasRun, setHasRun] = useState(false);
  const [metrics, setMetrics] = useState<PageWeightMetrics | undefined>(
    undefined,
  );

  useEffect(() => {
    const isDev = process.env.NODE_ENV === "development";
    if (
      typeof window === "undefined" ||
      typeof performance === "undefined" ||
      !isDev
    ) {
      setWarnings([]);
      setHasRun(false);
      setMetrics(undefined);
      return;
    }

    setHasRun(false);
    setMetrics(undefined);
    let cancelled = false;

    function run() {
      if (cancelled) {
        return;
      }
      const messages: string[] = [];
      let transferBytes = 0;
      let requestCount = 0;
      let excludedResource: LargestResource | undefined;
      try {
        const resources = performance.getEntriesByType(
          "resource",
        ) as PerformanceResourceTiming[];
        for (const r of resources) {
          const size = r.transferSize;
          transferBytes += size;
          if (
            size > 0 &&
            (!excludedResource || size > excludedResource.transferSize)
          ) {
            const name = r.name ? (r.name.split("/").pop() ?? r.name) : "?";
            excludedResource = { name, transferSize: size };
          }
        }
        requestCount = resources.length;
        /** Exclude largest resource (app bundle) so we only measure resources we can control. */
        const transferBytesExcludingApp = excludedResource
          ? Math.max(0, transferBytes - excludedResource.transferSize)
          : transferBytes;
        if (transferBytesExcludingApp > maxTransferBytes) {
          const mb = (transferBytesExcludingApp / (1024 * 1024)).toFixed(1);
          messages.push(
            `Page Weight: Transfer ${mb}MB (excl. app bundle) exceeds acceptable ${maxTransferBytes / (1024 * 1024)}MB (${requestCount} requests).`,
          );
        }
        if (requestCount > maxRequests) {
          messages.push(
            `Page Weight: ${requestCount} requests exceeds acceptable ${maxRequests}.`,
          );
        }
      } catch {
        // ignore
      }
      // run() may be invoked after cleanup (e.g. from load event)
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- cancelled is set by effect cleanup
      if (!cancelled) {
        const transferBytesExcludingApp = excludedResource
          ? Math.max(0, transferBytes - excludedResource.transferSize)
          : transferBytes;
        setWarnings(messages);
        setMetrics({
          transferBytes: transferBytesExcludingApp,
          requestCount,
          excludedResource,
        });
        setHasRun(true);
      }
    }

    if (document.readyState === "complete") {
      run();
    } else {
      window.addEventListener("load", run);
    }

    return () => {
      cancelled = true;
      window.removeEventListener("load", run);
    };
  }, [maxTransferBytes, maxRequests]);

  return { warnings, hasRun, metrics };
}
